// Small helpers for in-app sound + OS-level notifications on ticket events.
// Duplicated in apps/kitchen-display/src/lib/notify.ts — the two apps don't
// share a package, and this is small enough not to warrant one.
import { getAuthToken, syncApiUrl } from "@cliqsuite/db/powersync";

// A single reused <audio> element, not `new Audio(src)` per call — mobile
// browsers only carry an "unlocked" audio state forward to *later*
// programmatic .play() calls (e.g. a ticket going ready with nobody
// touching the screen) if the very first successful play happened on this
// same element, synchronously inside a real user gesture. A fresh
// `new Audio()` each time starts locked again every call — priming a
// throwaway instance (the old unlockAudioOnFirstInteraction did this, and
// its "unlocked" flag was never even read anywhere) doesn't unlock the one
// actually used later. Mirrors apps/kitchen-display/src/lib/notify.ts's fix.
let sharedAudio: HTMLAudioElement | null = null;

function getSharedAudio(): HTMLAudioElement {
  if (!sharedAudio) sharedAudio = new Audio();
  return sharedAudio;
}

/**
 * Must be called synchronously from inside a real user gesture — the
 * Push Notifications toggle's onClick (see SettingsScreen/PosApp). Plays
 * the shared element muted for an instant, purely to unlock it; nothing
 * audible happens. After this, playSound()'s later calls (triggered by a
 * ticket going ready, not a click) keep working for the rest of the page's
 * life, since they reuse this same now-unlocked element.
 */
export function unlockAudio(): void {
  const audio = getSharedAudio();
  audio.muted = true;
  audio.src = "/sounds/ticket-ready.mp3";
  void audio
    .play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;
    })
    .catch(() => {
      audio.muted = false;
    });
}

export function playSound(src: string) {
  const audio = getSharedAudio();
  audio.muted = false;
  audio.src = src;
  audio.currentTime = 0;
  audio.play().catch(() => {
    // Autoplay still blocked this session — the visual flash still lands the point.
  });
}

export type NotificationStatus = "granted" | "denied" | "default" | "unsupported";

export function getNotificationStatus(): NotificationStatus {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

export function requestNotificationPermission(): Promise<NotificationStatus> {
  if (typeof Notification === "undefined") return Promise.resolve("unsupported");
  if (Notification.permission !== "default") return Promise.resolve(Notification.permission);
  return Notification.requestPermission();
}

// Not a secret — this is meant to ship in the browser bundle. Paired with
// VAPID_PRIVATE_KEY on sync-api (see services/sync-api/src/lib/webpush.ts);
// if you ever rotate these, update both places (see .env.production.example).
const VAPID_PUBLIC_KEY = "BDFxPS9HabykC91SXTgh-QDVcRJMytnHSusG1vXmXLrtDpZjuVPsGcdPOQt5mYn2QNO5mfmCYHjqHMwXrNsyUPc";

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64Safe);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * Subscribes this device to real Web Push (via the service worker's own
 * push/notificationclick handlers in sw.ts) and registers the subscription
 * with sync-api against this device's id — this is what lets a "ready"
 * alert land even when the PWA is fully closed, not just backgrounded,
 * since the OS wakes the service worker independently of whether the page
 * is running. Requires Notification permission already granted; safe to
 * call on every mount (subscribing when already subscribed just returns
 * the existing subscription — no re-prompt, no duplicate).
 */
export async function subscribeToPush(deviceId: string): Promise<void> {
  if (Notification.permission !== "granted") return;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    await fetch(syncApiUrl("/push/subscribe"), {
      method: "POST",
      headers: { "Content-Type": "application/json", authorization: `Bearer ${getAuthToken() ?? ""}` },
      body: JSON.stringify({ deviceId, ...subscription.toJSON() }),
    });
  } catch (err) {
    console.error("Push subscribe failed:", err);
  }
}
